import { useEffect, useState } from 'react'
import { collection, onSnapshot, orderBy, query, where, type Firestore } from 'firebase/firestore'
import { ensureOwnerRealtime, ensureStaffRealtime } from '../lib/realtimeAuth'
import { api } from '../services/api'
import { useStaffAuth } from '../store/useStaffAuth'
import type { Order, CartItem } from '../store/useStore'

/** Firestore の Timestamp も API の ISO 文字列も同じ形に落とす (#1641 の API 取得で両方来る)。 */
function toIso(value: unknown): string | undefined {
  const ts = value as { toDate?: () => Date } | string | undefined
  if (typeof ts === 'string') return ts
  return ts?.toDate?.()?.toISOString?.()
}

/**
 * 注文 doc / API の 1 件を画面の `Order` に写す。**この列挙は whitelist** なので、
 * 足し忘れた項目は常に undefined になる (#1393 の「退店を押しても卓が消えない」は
 * これが原因だった)。購読と API 取得の両方から呼ぶので、片方だけ直さないこと。
 */
function mapOrder(id: string, data: Record<string, unknown>): Order {
  const d = data as Record<string, any>  // eslint-disable-line @typescript-eslint/no-explicit-any
  return {
    id,
    storeId: d.storeId,
    customerId: d.customerId,
    customerName: d.customerName,
    placedBy: d.placedBy === 'staff' ? 'staff' : 'customer',
    // #1400 誰が入力したか。作成時に焼き込んだ表示名（名簿の照会は
    // owner|manager 限定なので、バイトが見る画面では引けない）。
    placedByStaffName: typeof d.placedByStaffName === 'string' ? d.placedByStaffName : undefined,
    items: (d.items || []) as CartItem[],
    totalAmount: d.totalAmount ?? 0,
    status: d.status,
    paid: !!d.paid,
    // #1214 呼び出し番号 (A-01)。店側の注文管理・カウンター画面で使う。
    callNumber: d.callNumber,
    // #941/#942 現金会計
    paymentMethod: d.paymentMethod,
    cashPending: !!d.cashPending,
    // #956 Phase 2 卓単位一括現金会計: 代表客の id（卓一括 cash の目印）
    cashPayerId: d.cashPayerId,
    tableNumber: d.tableNumber,
    // #968 来店人数（卓セッションから注文に複製済）。
    partySize: typeof d.partySize === 'number' ? d.partySize : undefined,
    notes: d.notes,
    paymentIntentId: d.paymentIntentId,
    // #1589 会計キー。現金決済はこれが唯一の手がかり。
    settlementId: d.settlementId,
    // #2344 その注文の卓について、この会計で受け取った額 (お通し込み・値引き後)。
    // ここに入れ忘れると退店待ちカードが注文の合計へ縮退する — この mapper は
    // 書いた項目しか通さない (spread していない)
    settlementTotal: typeof d.settlementTotal === 'number' ? d.settlementTotal : undefined,
    paidAt: toIso(d.paidAt),
    // #1393 退店時刻。
    tableClearedAt: toIso(d.tableClearedAt),
    missingReported: !!d.missingReported,
    remadeFromOrderId: d.remadeFromOrderId,
    // #284 delivery fields — explicitly mapped because the previous
    // spread-less copy was stripping them, causing the owner UI to
    // mis-classify delivery orders as dine-in.
    orderType: d.orderType,
    deliveryAddress: d.deliveryAddress,
    deliveryNotes: d.deliveryNotes,
    deliveryLat: typeof d.deliveryLat === 'number' ? d.deliveryLat : undefined,
    deliveryLng: typeof d.deliveryLng === 'number' ? d.deliveryLng : undefined,
    captured: typeof d.captured === 'boolean' ? d.captured : undefined,
    cancelledBy: d.cancelledBy,
    cancelReason: d.cancelReason,
    unreadCountForCustomer: typeof d.unreadCountForCustomer === 'number' ? d.unreadCountForCustomer : 0,
    unreadCountForOwner: typeof d.unreadCountForOwner === 'number' ? d.unreadCountForOwner : 0,
    createdAt: toIso(d.createdAt) ?? '',
    updatedAt: toIso(d.updatedAt) ?? '',
  }
}

/**
 * Realtime subscription to orders for a given store (#289 owner side).
 * Pending (unpaid) orders are excluded — those are customer-only state
 * until /confirm flips them to 'received'.
 *
 * #1641 注文の直読は「当事者だけ」に閉じたので、購読の前にトークンを用意する。
 * オーナーは `storeIds` クレーム (既定のアプリ)、スタッフは `staffStoreId` を載せた
 * カスタムトークン (購読専用アプリ)。用意できない / 拒否されたときは API 取得に
 * 倒す — **厨房ボードが黙って白紙になるのが一番まずい**ため。
 */
export function useStoreOrders(storeId: string | null | undefined) {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(!!storeId)
  const staffSession = useStaffAuth((s) => s.session)
  const staffKey = staffSession ? `${staffSession.storeId}:${staffSession.memberId}` : null
  const staffMemberId = staffSession?.memberId ?? null
  const [db, setDb] = useState<Firestore | null>(null)
  const [fallback, setFallback] = useState(false)

  useEffect(() => {
    if (!storeId) return
    let cancelled = false
    const ready = staffMemberId
      ? ensureStaffRealtime(storeId, staffMemberId)
      : ensureOwnerRealtime(storeId)
    ready
      .then((resolved) => {
        if (cancelled) return
        setDb(resolved)
        if (!resolved) setFallback(true)
      })
      .catch((e: unknown) => {
        console.error('[useStoreOrders] realtime auth failed:', e)
        if (cancelled) return
        setDb(null)
        setFallback(true)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, staffKey, staffMemberId])

  const [trackedStoreId, setTrackedStoreId] = useState(storeId)
  if (storeId !== trackedStoreId) {
    setTrackedStoreId(storeId)
    setOrders([])
    setLoading(!!storeId)
    // #1641 店が変わったら購読先もやり直す (前の店のトークンで繋がない)
    setDb(null)
    setFallback(false)
  }

  // #1641 購読が使えないときの API 取得。直読と同じ並び (新しい順) で 10 秒ごと。
  useEffect(() => {
    if (!storeId || !fallback) return
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.listStoreOrders(storeId)
        if (cancelled) return
        const list = ((res as { orders?: unknown[] }).orders || []) as Record<string, unknown>[]
        setOrders(list.map((o) => mapOrder(String(o.id), o)))
      } catch (e) {
        console.error('[useStoreOrders] fallback fetch failed:', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 10_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [storeId, fallback])

  useEffect(() => {
    if (!storeId || !db || fallback) return

    const q = query(
      collection(db, 'orders'),
      where('storeId', '==', storeId),
      orderBy('createdAt', 'desc'),
    )

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list: Order[] = []
        snap.forEach((d) => list.push(mapOrder(d.id, d.data())))
        setOrders(list)
        setLoading(false)
      },
      (err) => {
        console.error('[useStoreOrders] subscription error:', err)
        setLoading(false)
        // #1641 拒否されたら API に切り替える (権限の用意に失敗した端末でも現場を止めない)
        if ((err as { code?: string }).code === 'permission-denied') setFallback(true)
      },
    )

    return () => unsubscribe()
  }, [storeId, db, fallback])

  const received = orders.filter((o) => o.status === 'received')
  const preparing = orders.filter((o) => o.status === 'preparing')
  const served = orders.filter((o) => o.status === 'served')
  const cancelled = orders.filter((o) => o.status === 'cancelled')

  return { orders, received, preparing, served, cancelled, loading }
}
